import { apiFetch } from './auth-service';

export interface PitchSubmissionDoc {
  _id?: string;
  type: 'initial' | 'final';
  startupName: string;
  website: string;
  problem: string;
  solution: string;
  targetMarket: string;
  marketSize: string;
  industry: string;
  geographicFocus: string;
  stage: string;
  revenueModel: string;
  traction: string;
  teamSize: string;
  fundingStage: string;
  fundingAsk: string;
  equityOffered: string;
  useOfFunds: string;
  competitors: string;
  differentiation: string;
  milestones: string;
  slideDeckUrl: string;
  slideDeckKey: string;
  slideDeckName: string;
  slideDeckSize: number;
  isSubmitted: boolean;
  submittedAt: string | null;
  reviewStatus?: 'pending' | 'revision_needed' | 'rejected' | 'approved' | 'approved_distinction';
  score?: number | null;
  mentorFeedback?: string;
  gradedAt?: string | null;
  submissionCount?: number;
  updatedAt?: string;
}

export interface FinalPitchForReview extends PitchSubmissionDoc {
  userId: { _id: string; name: string; email: string };
  gradedBy?: { _id: string; name: string } | null;
}

const EMPTY_FIELDS: Omit<PitchSubmissionDoc, 'type'> = {
  startupName: '', website: '',
  problem: '', solution: '', targetMarket: '', marketSize: '', industry: '',
  geographicFocus: '', stage: '', revenueModel: '', traction: '', teamSize: '',
  fundingStage: '', fundingAsk: '', equityOffered: '', useOfFunds: '',
  competitors: '', differentiation: '', milestones: '',
  slideDeckUrl: '', slideDeckKey: '', slideDeckName: '', slideDeckSize: 0,
  isSubmitted: false, submittedAt: null,
};

export const emptyPitch = (type: 'initial' | 'final'): PitchSubmissionDoc => ({ ...EMPTY_FIELDS, type });

export const getPitch = (type: 'initial' | 'final'): Promise<PitchSubmissionDoc | null> =>
  apiFetch.get<{ pitch: PitchSubmissionDoc | null }>(`/pitch/${type}`).then(r => r.pitch);

export const savePitch = (type: 'initial' | 'final', data: Partial<PitchSubmissionDoc>): Promise<PitchSubmissionDoc> =>
  apiFetch.put<{ pitch: PitchSubmissionDoc }>(`/pitch/${type}`, data).then(r => r.pitch);

export const presignDeck = (
  type: 'initial' | 'final',
  fileName: string,
  contentType: string,
): Promise<{ presignedUrl: string; fileKey: string; fileUrl: string }> =>
  apiFetch.post(`/pitch/${type}/deck/presign`, { fileName, contentType });

export const saveDeck = (
  type: 'initial' | 'final',
  meta: { fileKey: string; fileUrl: string; fileName: string; fileSize: number },
): Promise<PitchSubmissionDoc> =>
  apiFetch.post<{ pitch: PitchSubmissionDoc }>(`/pitch/${type}/deck/save`, meta).then(r => r.pitch);

export const deleteDeck = (type: 'initial' | 'final'): Promise<PitchSubmissionDoc> =>
  apiFetch.delete<{ pitch: PitchSubmissionDoc }>(`/pitch/${type}/deck`).then(r => r.pitch);

export const listFinalPitchesForReview = (): Promise<FinalPitchForReview[]> =>
  apiFetch.get<{ pitches: FinalPitchForReview[] }>('/pitch/review/final/all').then(r => r.pitches);

export const getFinalPitchForReview = (id: string): Promise<FinalPitchForReview> =>
  apiFetch.get<{ pitch: FinalPitchForReview }>(`/pitch/review/final/${id}`).then(r => r.pitch);

export const getFinalPitchDeckUrl = (id: string): Promise<{ url: string; fileName: string }> =>
  apiFetch.get(`/pitch/review/final/${id}/deck`);

export const gradeFinalPitch = (
  id: string,
  data: { score: number; mentorFeedback: string },
): Promise<PitchSubmissionDoc> =>
  apiFetch.post<{ pitch: PitchSubmissionDoc }>(`/pitch/review/final/${id}/grade`, data).then(r => r.pitch);
